import { ReactComponent as FilterSvg } from 'images/icons/filter.svg'
import { ReactComponent as DownArrowSvg } from 'images/icons/arrowDown.svg'
import { ReactComponent as UpArrowSvg } from 'images/icons/arrowUp.svg'
import { ReactComponent as CheckFilterSvg } from 'images/icons/checkFilter.svg'
import { Popover } from '@mui/material'
import { useRef, useState } from 'react'
import { ITaskFilters } from 'utils/interfaces'
import { useTranslation } from 'react-i18next'
import style from './TaskFilter.module.css'

const FiltersList = ({
  filters,
  onFiltersChanged,
}: {
  filters: ITaskFilters
  onFiltersChanged: (filters: ITaskFilters) => void
}) => {
  const { t } = useTranslation()
  const onActiveClicked = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newFilters = { ...filters }
    newFilters.status = newFilters.status === 'active' ? 'all' : 'active'
    onFiltersChanged(newFilters)
  }

  const onCompletedClicked = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newFilters = { ...filters }
    newFilters.status = newFilters.status === 'completed' ? 'all' : 'completed'
    onFiltersChanged(newFilters)
  }

  const onMyTasksClicked = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newFilters = { ...filters }
    newFilters.user = newFilters.user === 'me' ? 'all' : 'me'
    onFiltersChanged(newFilters)
  }

  const onVideoClicked = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newFilters = { ...filters }
    newFilters.video = newFilters.video === 'current' ? 'all' : 'current'
    onFiltersChanged(newFilters)
  }

  const onInTimeClicked = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newFilters = { ...filters }
    newFilters.date = newFilters.date === 'inTime' ? 'all' : 'inTime'
    onFiltersChanged(newFilters)
  }

  const onOutdatedClicked = (e: React.MouseEvent) => {
    e.stopPropagation()
    const newFilters = { ...filters }
    newFilters.date = newFilters.date === 'outdated' ? 'all' : 'outdated'
    onFiltersChanged(newFilters)
  }

  return (
    <div className={style.filtersList}>
      <div className={style.filterBlock}>
        <div className={style.filterTitle}>{t('video.task_filter.status')}</div>
        <div className={style.filterItem} onClick={onActiveClicked}>
          {t('video.task_filter.active')} {filters.status === 'active' && <CheckFilterSvg className={style.check} />}
        </div>
        <div className={style.filterItem} onClick={onCompletedClicked}>
          {t('video.task_filter.completed')}{' '}
          {filters.status === 'completed' && <CheckFilterSvg className={style.check} />}
        </div>
      </div>
      <div className={style.filterBlock}>
        <div className={style.filterTitle}>{t('video.task_filter.user')}</div>
        <div className={style.filterItem} onClick={onMyTasksClicked}>
          {t('video.task_filter.my_tasks')} {filters.user === 'me' && <CheckFilterSvg className={style.check} />}
        </div>
      </div>
      <div className={style.filterBlock}>
        <div className={style.filterTitle}>{t('video.task_filter.video')}</div>
        <div className={style.filterItem} onClick={onVideoClicked}>
          {t('video.task_filter.current_video')}{' '}
          {filters.video === 'current' && <CheckFilterSvg className={style.check} />}
        </div>
      </div>
      <div className={style.filterBlock}>
        <div className={style.filterTitle}>{t('video.task_filter.date')}</div>
        <div className={style.filterItem} onClick={onInTimeClicked}>
          {t('video.task_filter.in_time')} {filters.date === 'inTime' && <CheckFilterSvg className={style.check} />}
        </div>
        <div className={style.filterItem} onClick={onOutdatedClicked}>
          {t('video.task_filter.overdue')} {filters.date === 'outdated' && <CheckFilterSvg className={style.check} />}
        </div>
      </div>
    </div>
  )
}

const TaskFilter = ({
  filters,
  onFiltersChanged,
}: {
  filters: ITaskFilters
  onFiltersChanged: (filters: ITaskFilters) => void
}) => {
  const { t } = useTranslation()
  const [showFilters, setShowFilters] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const filtersArray: string[] = []
  const filterText = () => {
    if (filters.status === 'active') {
      filtersArray.push(t('video.task_filter.active'))
    } else if (filters.status === 'completed') {
      filtersArray.push(t('video.task_filter.completed'))
    }
    if (filters.user === 'me') {
      filtersArray.push(t('video.task_filter.my_tasks'))
    }
    if (filters.date === 'outdated') {
      filtersArray.push(t('video.task_filter.overdue'))
    } else if (filters.date === 'inTime') {
      filtersArray.push(t('video.task_filter.in_time'))
    }
    if (filters.video === 'current') {
      filtersArray.push(t('video.task_filter.current_video'))
    }
    const result = filtersArray.length ? filtersArray.join(', ') : t('video.task_filter.all_tasks')
    return result
  }

  return (
    <div className={style.row} onClick={() => setShowFilters(!showFilters)} ref={ref}>
      <FilterSvg />
      <div className={style.text}>{filterText()}</div>
      {showFilters ? <UpArrowSvg /> : <DownArrowSvg />}
      <Popover
        open={showFilters}
        anchorEl={ref.current}
        onClose={() => setShowFilters(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            marginTop: '10px',
            borderRadius: '0px',
          },
        }}
      >
        <FiltersList filters={filters} onFiltersChanged={onFiltersChanged} />
      </Popover>
    </div>
  )
}

export default TaskFilter
